"use client";

import { COOKIE_NAME_THEME, type Theme } from "@/utils/helpers";
import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useState,
} from "react";
import { useCookies } from "react-cookie";

type ThemeProviderContext = {
  theme: Theme;
  setTheme: (theme: Theme) => void;
};

const Context = createContext<ThemeProviderContext | undefined>(undefined);

/**
 * Dark mode is an INTERNAL-ONLY EXPERIMENT behind the `dark-mode` flag.
 * New features are not expected to support it. See REVIEW.md section 31 and
 * components/providers/DARK_MODE.md before adding anything theme-aware.
 *
 * Applies the `.dark` class on <html> so Tailwind `dark:` variants and the
 * `.dark` CSS variable block cover portals (dialogs, popovers) on body.
 */
export default function ThemeProvider({
  initialTheme,
  children,
}: {
  initialTheme: Theme;
  children: React.ReactNode;
}) {
  const [, setCookie] = useCookies([COOKIE_NAME_THEME]);
  const [theme, setThemeState] = useState<Theme>(initialTheme);

  useLayoutEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
    // Respondent-facing routes share <html> and keep their own theming.
    return () => document.documentElement.classList.remove("dark");
  }, [theme]);

  const setTheme = useCallback(
    (nextTheme: Theme) => {
      setThemeState(nextTheme);
      setCookie(COOKIE_NAME_THEME, nextTheme, {
        sameSite: "strict",
        // `secure` cookies are rejected over http (local dev); only set on https.
        secure: window.location.protocol === "https:",
        path: "/",
        maxAge: 60 * 60 * 24 * 400, // 400 days, max recommended age
      });
    },
    [setCookie]
  );

  return (
    <Context.Provider value={{ theme, setTheme }}>
      {/* Runs before first paint so a dark-cookie page load doesn't flash light. */}
      {initialTheme === "dark" && (
        <script
          dangerouslySetInnerHTML={{
            __html: 'document.documentElement.classList.add("dark")',
          }}
        />
      )}
      {children}
    </Context.Provider>
  );
}

export const useTheme = () => {
  const context = useContext(Context);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};
